import React from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import Icon, { iconNames } from "../../icons";

export interface MenuItemData {
  menuId: number;
  menuName: string;
  menuCode: string;
  icon?: string;
  url?: string;
  read?: boolean;
  write?: boolean;
  delete?: boolean;
  submenu?: MenuItemData[];
}

interface MenuItemProps {
  menu: MenuItemData;
  depth: number;
  /** Maximum depth at which children accordion-expand inline. Default: Infinity */
  maxDepth?: number;
  expandedIds: Set<number>;
  toggleExpanded: (id: number) => void;
  /** Called when a leaf node (no children) is clicked */
  onNavigate: (url: string) => void;
  /**
   * When provided, overrides the entire click behaviour.
   * Used for items like "Settings" that should enter a special mode
   * instead of accordion-expanding. An arrow indicator is shown.
   */
  onOverrideClick?: (menu: MenuItemData) => void;
  DISABLED_MENU_ITEMS: string[];
  DELETED_MENU_ITEMS: string[];
  pathname: string;
  isAIMenu?: boolean;
}

/**
 * Sidebar menu names come from the backend (`menuCode`/`menuName`), not from
 * frontend strings, so they can't be translated via a normal t() call on
 * static text. This looks up the translation by `menuCode` in the `menu`
 * namespace and falls back to the raw backend `menuName` for any code that
 * doesn't have a translation yet (e.g. a menu item added after this map).
 */
export const getMenuDisplayName = (menu: MenuItemData, t: TFunction): string =>
  t(menu.menuCode, { ns: "menu", defaultValue: menu.menuName });

/** Returns true if this menu item or any descendant matches the current path */
export const isMenuActive = (menu: MenuItemData, pathname: string): boolean => {
  if (menu.url && menu.url !== "N/A" && menu.url !== "#") {
    if (pathname === menu.url) return true;
    if (menu.url.length > 1 && pathname.startsWith(menu.url + "/")) return true;
  }
  if (menu.submenu && menu.submenu.length > 0) {
    return menu.submenu.some((child) => isMenuActive(child, pathname));
  }
  return false;
};

const MenuItem: React.FC<MenuItemProps> = ({
  menu,
  depth,
  maxDepth = Infinity,
  expandedIds,
  toggleExpanded,
  onNavigate,
  onOverrideClick,
  DISABLED_MENU_ITEMS,
  DELETED_MENU_ITEMS,
  pathname,
  isAIMenu = false,
}) => {
  const { t } = useTranslation("menu");
  const displayName = getMenuDisplayName(menu, t);
  const hasChildren = !!(menu.submenu && menu.submenu.length > 0);
  /** At maxDepth, treat item as a leaf — navigate instead of expanding */
  const canExpandDepth = depth < maxDepth;
  const isExpanded = expandedIds.has(menu.menuId);
  // At maxDepth, don't check descendants — only match this item's own URL
  const isActive =
    !canExpandDepth && hasChildren
      ? !!(
          menu.url &&
          menu.url !== "N/A" &&
          menu.url !== "#" &&
          (pathname === menu.url ||
            (menu.url.length > 1 && pathname.startsWith(menu.url + "/")))
        )
      : isMenuActive(menu, pathname);
  const isExactActive =
    !!menu.url &&
    menu.url !== "N/A" &&
    menu.url !== "#" &&
    pathname === menu.url;

  const isDisabled =
    (!menu.read && !menu.write && !menu.delete) ||
    DISABLED_MENU_ITEMS.includes(menu.menuCode);

  const filteredChildren = hasChildren
    ? menu.submenu!.filter(
        (child) => !DELETED_MENU_ITEMS.includes(child.menuCode)
      )
    : [];

  const handleClick = () => {
    if (isDisabled) return;
    if (onOverrideClick) {
      onOverrideClick(menu);
      return;
    }
    if (hasChildren && canExpandDepth) {
      toggleExpanded(menu.menuId);
    } else if (menu.url && menu.url !== "N/A" && menu.url !== "#") {
      onNavigate(menu.url);
    }
  };

  /* ── Styles ── */
  const aiBase = isActive
    ? "bg-gradient-to-r from-[#7B5CFF] to-[#1ED3FF] text-white shadow-lg shadow-[#7B5CFF]/40"
    : "bg-gradient-to-r from-[#7B5CFF]/80 to-[#1ED3FF]/80 text-white hover:from-[#6A4AF5] hover:to-[#14C4EE] shadow-md";

  const normalBase =
    isExactActive || (!canExpandDepth && isActive)
      ? "bg-white text-secondary font-semibold"
      : isActive && !hasChildren
      ? "bg-white/60 text-secondary"
      : isActive
      ? "text-secondary"
      : "text-gray-700 hover:bg-gray-300";

  const itemClass = [
    "w-full flex items-center gap-2 text-left transition-colors duration-150 relative overflow-hidden",
    depth === 0 ? "h-[48px] px-3" : "h-[36px]",
    isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
    isAIMenu ? aiBase : normalBase,
  ].join(" ");

  const iconColor = isAIMenu ? "#FFFFFF" : isActive ? "currentColor" : "#374151";

  const arrowColor = isAIMenu ? "#ffffff99" : isActive ? "currentColor" : "#9CA3AF";

  /* ── Whether children expand inline (no override, within depth limit) ── */
  const canExpand = hasChildren && !onOverrideClick && canExpandDepth;

  return (
    <div>
      <button
        onClick={handleClick}
        disabled={isDisabled}
        title={displayName}
        className={itemClass}
        style={depth > 0 ? { paddingLeft: 8 + depth * 14, paddingRight: 8 } : undefined}
        data-menu-active={isExactActive ? "true" : undefined}
      >
        {/* AI shimmer */}
        {isAIMenu && (
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-30 animate-pulse pointer-events-none" />
        )}

        {/* Icon — top level only */}
        {depth === 0 && (
          <span className="flex-shrink-0 w-6 flex items-center justify-center z-10">
            <Icon
              name={(menu.icon || "settings") as iconNames}
              size={18}
              color={iconColor}
            />
          </span>
        )}

        {/* Indent connector for nested items */}
        {depth > 0 && (
          <span className="flex-shrink-0 w-2.5 border-t border-gray-300 mt-px" />
        )}

        {/* Label */}
        <span className="flex-grow text-sm truncate z-10 flex items-center gap-1">
          <span>{displayName}</span>
          {isAIMenu && <Icon name="ai-stars" size={12} color="#FFD700" />}
        </span>

        {/* Expand / collapse arrow (accordion items) */}
        {canExpand && (
          <span className="flex-shrink-0 z-10">
            <Icon
              name={isExpanded ? "arrowup" : "arrowdown"}
              size={13}
              color={arrowColor}
            />
          </span>
        )}

        {/* Forward arrow (override items like Settings) */}
        {onOverrideClick && (
          <span className="flex-shrink-0 z-10">
            <Icon name="arrowright" size={13} color={arrowColor} />
          </span>
        )}
      </button>

      {/* Inline children (accordion) */}
      {canExpand && isExpanded && filteredChildren.length > 0 && (
        <div
          className={
            depth === 0
              ? "border-l-2 border-secondary/20 ml-6"
              : "border-l border-gray-200 ml-5"
          }
        >
          {filteredChildren.map((child) => (
            <MenuItem
              key={child.menuId}
              menu={child}
              depth={depth + 1}
              maxDepth={maxDepth}
              expandedIds={expandedIds}
              toggleExpanded={toggleExpanded}
              onNavigate={onNavigate}
              DISABLED_MENU_ITEMS={DISABLED_MENU_ITEMS}
              DELETED_MENU_ITEMS={DELETED_MENU_ITEMS}
              pathname={pathname}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MenuItem;
